import clsx from 'clsx'
import type { ReactNode } from 'react'
import { View } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  type FlatListPropsWithLayout,
} from 'react-native-reanimated'

import { Image } from '@/src/elements/image'
import { ResponsiveDimensions } from '@/src/general/components/ResponsiveDimensions'
import { ResponsiveDisplay } from '@/src/general/components/ResponsiveDisplay'
import { TEST_ID } from '@/src/general/constants/testId'
import { useHeaderAnimation } from '@/src/general/hooks/useHeaderAnimation'

import { AnimatedParallaxListHeader } from './AnimatedParallaxListHeader'

export type TAnimatedParallaxListProps<T> = {
  imageUri: string
  data: T[] | undefined
  ListHeaderComponent: typeof AnimatedParallaxListHeader
  ImageComponent?: () => ReactNode
} & FlatListPropsWithLayout<T>

const List = <T extends object>({
  imageUri,
  data,
  ListHeaderComponent,
  ImageComponent,
  ...props
}: TAnimatedParallaxListProps<T>) => {
  const { scrollHandler, offsetY } = useHeaderAnimation()

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        scaleY: interpolate(offsetY.value, [0, 100], [1.35, 1.2], Extrapolation.CLAMP),
      },
      {
        scaleX: interpolate(offsetY.value, [0, 100], [1.35, 1.2], Extrapolation.CLAMP),
      },
      {
        translateY: interpolate(offsetY.value, [-150, 0, 150], [130, 0, -70]),
      },
    ],
  }))

  return (
    <View className="min-w-screen flex-1">
      <ResponsiveDisplay size="xs" behavior="maxWidth">
        <View className="absolute h-1/3 min-h-96 min-w-full items-center justify-center overflow-hidden md:relative md:mb-6 md:px-5">
          <Animated.View
            testID={TEST_ID.MISCELLANEOUS.PARALLAX_FLAT_LIST_ANIMATED_IMAGE_MOBILE}
            style={[
              {
                top: 0,
                position: 'absolute',
                width: 500,
                height: 240,
              },
              animatedStyle,
            ]}
          >
            {!ImageComponent && (
              <Image
                className="rounded-none md:rounded-3xl"
                contentFit="cover"
                source={{ uri: imageUri }}
                testID={TEST_ID.MISCELLANEOUS.PARALLAX_FLAT_LIST_IMAGE_MOBILE}
              />
            )}
            {ImageComponent && <ImageComponent />}
          </Animated.View>
        </View>
      </ResponsiveDisplay>

      <Animated.FlatList
        testID={TEST_ID.MISCELLANEOUS.PARALLAX_FLAT_LIST}
        indicatorStyle="white"
        className="max-h-screen"
        scrollEventThrottle={16}
        onScroll={scrollHandler}
        ItemSeparatorComponent={({ index }) => (
          <ResponsiveDimensions>
            {({ xs }) => (
              <View testID={`parallax-list-separator-${index}`} className={clsx('h-2 w-full', { 'bg-white': xs })} />
            )}
          </ResponsiveDimensions>
        )}
        contentContainerClassName="w-full max-w-full"
        data={data}
        ListHeaderComponent={ListHeaderComponent}
        {...props}
      />
    </View>
  )
}

export const AnimatedParallax = {
  List,
  Header: AnimatedParallaxListHeader,
}
